import { forwardRef, useEffect, useRef } from "react";
import { Controller, useFormContext } from "react-hook-form";
import { useParams } from "react-router-dom";
import { MDBCol, MDBRow } from "mdbreact";

import { Button, Label, TextArea } from "abs-ui-components";

import FormField from "../../../../../common/form/Form";
import { callApi } from "../../../../../services/MonitoringPlanService";
import { API_ROUTES } from "../../ApiRoutes/ApiRoutes";
import { emitToasterMsg } from "../../../../../services/toasterUtility";
import { AUTOSAVE_SUCCESS_MESSAGE, C1Column } from "../../Constants";
import AutoSave from "../../../../../common/AutoSave/AutoSave";

const C1ConditionsOfExemption = forwardRef(({ c1ExemptionConditionsData, vesselMstId,refetchMonitoringData, dropdownData }, ref) => {
    const { control, reset, getValues, formState } = useFormContext();
    
    const lastManualSave = useRef(null);
    const manualSaveTriggered = useRef(false);
    const savedValuesRef = useRef({});

    const { dirtyFields } = formState;
    const isC1Dirty = !!(dirtyFields?.c1 && Object.keys(dirtyFields.c1).length > 0);

    useEffect(() => {
        if (c1ExemptionConditionsData) {
            const cleanedValues = C1Column.reduce((acc, { name }) => {
                acc[name] = c1ExemptionConditionsData?.[name] || "";
                return acc;
            }, {});
            reset(prev => ({ ...prev, c1: cleanedValues }));
            savedValuesRef.current = cleanedValues;
        }
    }, [c1ExemptionConditionsData, reset]);

    const buildOptions = (source, firstLabel = "Select an option") => [
        { label: firstLabel, value: "" },
        ...(Array.isArray(source) ? source : []).map(item => ({
            label: item.value,
            value: item.code,
        })),
    ];

    const yesNoNotApplicableOptions = buildOptions(
        dropdownData?.yesNoNotApplicableOptions
    );

    const yesNoOptions = buildOptions(
        dropdownData?.yesNoOptions
    );

    AutoSave(ref, [], async () => {
        const currentValues = getValues("c1");
        const isChanged = Object.keys(currentValues || {}).some(
            key => currentValues[key] !== savedValuesRef.current?.[key]
        );
        if (!isC1Dirty || !isChanged) return;
        await handleSave(true);
    });

    const handleSave = async (isAuto = false) => {
        const formValues = getValues("c1");
        const payload = C1Column.reduce((acc, { name }) => {
            const value = formValues?.[name];
            acc[name] = value || "";
            return acc;
        }, {});
        try {
            const C1ConditionsOfExemption = await callApi({
                method: "POST",
                url: API_ROUTES.C1(vesselMstId),
                data: payload,
            });
            if (C1ConditionsOfExemption?.isSuccess) {
                emitToasterMsg("success", "Table C.1", isAuto ? AUTOSAVE_SUCCESS_MESSAGE : "Created successfully");
                reset(prev => ({ ...prev, c1: formValues }));
                savedValuesRef.current = { ...formValues };
                if (!isAuto) {
                    lastManualSave.current = Date.now();
                }
                if (typeof refetchMonitoringData === "function") {
                    await refetchMonitoringData();
                }
            }
            else {
                emitToasterMsg("error", "Table C.1", C1ConditionsOfExemption?.errorMessage);
            }
        } catch (error) {
            emitToasterMsg("error", "Table C.1", error?.errorMessage);
        }
    };

    return (
        <div className="C1-section-form mt-4">
            {C1Column.map(({ label, name, maxLength, type }, index) => (
                <MDBRow className="align-items-center mb-3" key={index}>
                    <MDBCol md="4">
                        <Label name={label} bold />
                    </MDBCol>
                    <MDBCol md="4">
                        {type === "dropdown" ? (
                            <FormField
                                name={`c1.${name}`}
                                type="dropdown"
                                options={
                                    name === "expectedVoyagesUnderScope" || name === "regultionCondition"
                                        ? yesNoOptions
                                        : name === "useOfDerogation"
                                            ? yesNoNotApplicableOptions
                                            : []
                                }
                            />
                        ) : (
                            <Controller
                                name={`c1.${name}`}
                                control={control}
                                render={({ field }) => (
                                    <TextArea
                                        id={`c1_${name}`}
                                        className="textarea-resize"
                                        value={field.value || ""}
                                        onChange={field.onChange}
                                        placeholder=""
                                        maxLength={maxLength}
                                        rows={1}
                                    />
                                )}
                            />
                        )}
                    </MDBCol>
                </MDBRow>
            ))}

            <div className="text-center">
                <Button id="c1_save_btn"
                    disabled={!isC1Dirty}
                    onClick={async () => {
                        manualSaveTriggered.current = true;
                        await handleSave(false);
                    }}>
                    Save
                </Button>
            </div>
        </div>
    );
});

export default C1ConditionsOfExemption;
